import { apiFetch } from '@/lib/api/client';
import type { MediaRow } from '@/routes/media/types';

import { SESSION_STATUSES, type SessionStatus } from './session';

/**
 * The `/admin/auth/*` contracts from docs/04-API-SPEC.md §5.
 *
 * Hand-written rather than taken from `@sjuni/types`. The generated operations
 * describe the token envelope correctly, but `AuthUserResource` reaches the
 * schema through `->resolve()` on an array literal, so it publishes as
 * `readonly unknown[]` — the same Scramble blind spot the public resources hit
 * with `translatedStrings()`. The shapes below are the ones the controllers
 * actually return, checked against `AuthUserResource::toArray()`.
 */

/** `AdminTokens::payload()`. */
export interface TokenPayload {
  token: string;
  token_type: 'Bearer';
  expires_at: string | null;
  status: SessionStatus;
}

export interface AdminUser {
  id: number;
  name: string;
  email: string;
  locale: string;
  is_active: boolean;
  last_login_at: string | null;
  avatar_media_id: number | null;
  /** The account's own picture, or null. Drawn in the header and the users list. */
  avatar: MediaRow | null;
  faculty?: { id: number | null; slug: string | null; name: string | null };
  roles: string[];
  /**
   * Flattened by the server so the RBAC matrix lives in one place. §5 is
   * explicit that this is "a convenience for rendering, never an authorisation
   * decision" — every route is still guarded by a Policy.
   */
  permissions: string[];
  two_factor: { required: boolean; confirmed: boolean };
}

export interface LoginResult extends TokenPayload {
  user?: AdminUser;
}

export function isSessionStatus(value: unknown): value is SessionStatus {
  return SESSION_STATUSES.includes(value as SessionStatus);
}

export function login(credentials: { email: string; password: string }): Promise<LoginResult> {
  return apiFetch<LoginResult>('/admin/auth/login', {
    method: 'POST',
    body: credentials,
    anonymous: true,
  });
}

/**
 * Either a TOTP code or one of the eight recovery codes. The server requires
 * exactly one and says so per field, so both are sent as given.
 */
export function answerTwoFactorChallenge(payload: {
  code?: string;
  recovery_code?: string;
}): Promise<LoginResult> {
  return apiFetch<LoginResult>('/admin/auth/two-factor-challenge', {
    method: 'POST',
    body: payload,
    // The challenge token is the credential here, so a 401 means it expired
    // mid-flow — the login screen is the right destination, not a modal.
    anonymous: true,
  });
}

export interface TwoFactorEnrolment {
  qr_code_svg: string;
  qr_code_url: string;
  secret: string;
  recovery_codes: string[];
  confirmed: boolean;
}

export function startTwoFactorEnrolment(): Promise<{ data: TwoFactorEnrolment }> {
  return apiFetch<{ data: TwoFactorEnrolment }>('/admin/auth/two-factor', {
    method: 'POST',
    anonymous: true,
  });
}

/**
 * Confirming from an *enrolment* token returns a full token as well, so a Super
 * Admin setting 2FA up does not have to sign in twice. Confirming from a full
 * one — someone turning it on from their profile — returns only `confirmed`,
 * and their existing session is left alone.
 */
export function confirmTwoFactor(code: string): Promise<{
  data: { confirmed: boolean };
  token?: string;
  token_type?: 'Bearer';
  expires_at?: string | null;
  status?: SessionStatus;
}> {
  return apiFetch('/admin/auth/two-factor/confirm', {
    method: 'POST',
    body: { code },
    anonymous: true,
  });
}

export function fetchMe(): Promise<{ data: AdminUser }> {
  return apiFetch<{ data: AdminUser }>('/admin/auth/me');
}

export function logout(): Promise<void> {
  return apiFetch<void>('/admin/auth/logout', { method: 'POST' });
}

export function requestPasswordReset(email: string): Promise<{ message: string }> {
  return apiFetch<{ message: string }>('/admin/auth/forgot-password', {
    method: 'POST',
    body: { email },
    anonymous: true,
  });
}

export function resetPassword(payload: {
  token: string;
  email: string;
  password: string;
  password_confirmation: string;
}): Promise<{ message: string }> {
  return apiFetch<{ message: string }>('/admin/auth/reset-password', {
    method: 'POST',
    body: payload,
    anonymous: true,
  });
}

/** `PUT /admin/auth/profile` — the `/profile` screen's own fields. */
export function updateProfile(fields: {
  name: string;
  locale: string;
  avatar_media_id: number | null;
}): Promise<{ data: AdminUser }> {
  return apiFetch<{ data: AdminUser }>('/admin/auth/profile', {
    method: 'PUT',
    body: fields,
  });
}
